import React, { useState, useEffect } from 'react';
import { Save, Building2, MapPin, User, UserCheck, ChevronRight, Briefcase, Plus, X, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { getLocalToday } from '../utils/dateUtils';
import DateInput from './DateInput';
import './UpdateEmployee.css';

const AddEstablishment = () => {
  const DRAFT_KEY = 'addOrganization_draft';
  const [activeTab, setActiveTab] = useState('basic');
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    // Basic Information
    establishmentName: '',
    code: '',
    contractStartDate: getLocalToday(),
    contractValidTill: '',
    tenure: '', // Auto-calculated

    // Tax/Compliance Information
    gstin: '',
    pan: '',
    esicCode: '',
    epfCode: '',

    // Bank Account Details
    bankName: '',
    bankBranch: '',
    bankAccountNumber: '',
    bankIfsc: '',

    // Main Establishment Address
    mainLocation: '',
    mainAddress: '',
    mainTelephone: '',
    mainFax: '',
    mainEmail: '',
    
    // Registered/Head Office Address
    registeredLocation: '',
    registeredAddress: '',
    registeredTelephone: '',
    registeredFax: '',
    registeredEmail: '',
    
    // Employer/Responsible Person Details
    employerName: '',
    employerDesignation: '',
    employerResidentialAddress: '',
    employerTelephone: '',
    employerMobile: '',
    employerEmail: '',
    
    // Manager/Authorised Signatory Details
    managerName: '',
    managerDesignation: '',
    managerResidentialAddress: '',
    managerTelephone: '',
    managerMobile: '',
    managerEmail: '',
    
    // Authorised Signatory (if different)
    authSignatoryName: '',
    authSignatoryDesignation: 'Authorised Signatory',
    authSignatoryResidentialAddress: '',
    authSignatoryTelephone: '',
    authSignatoryMobile: '',
    authSignatoryEmail: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const tabs = [
    { id: 'basic', name: 'Basic Info', icon: Building2 },
    { id: 'departments', name: 'Departments', icon: Briefcase },
    { id: 'addresses', name: 'Addresses', icon: MapPin },
    { id: 'employer', name: 'Employer Details', icon: User },
    { id: 'management', name: 'Management', icon: UserCheck }
  ];

  // Load draft from localStorage on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setFormData(draft.formData || formData);
        setDepartments(draft.departments || []);
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);

  const calculateTenure = (startDate, endDate) => {
    if (!startDate || !endDate) return '';

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) return 'Invalid dates';

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    const parts = [];
    if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
    if (days > 0 && years === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);

    return parts.length > 0 ? parts.join(', ') : '0 days';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // Auto-calculate tenure when dates change
      if (field === 'contractStartDate' || field === 'contractValidTill') {
        updated.tenure = calculateTenure(
          field === 'contractStartDate' ? value : prev.contractStartDate,
          field === 'contractValidTill' ? value : prev.contractValidTill
        );
      }

      return updated;
    });
  };

  const handleAddDepartment = () => {
    if (newDepartment.trim() && !departments.includes(newDepartment.trim())) {
      setDepartments([...departments, newDepartment.trim()]);
      setNewDepartment('');
    } else if (departments.includes(newDepartment.trim())) {
      alert('Department already exists');
    }
  };

  const handleRemoveDepartment = (dept) => {
    setDepartments(departments.filter(d => d !== dept));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDepartment();
    }
  };

  // Save draft to localStorage — no validation, all fields optional
  const handleSubmit = async () => {
    try {
      const draft = {
        formData,
        departments,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      alert('Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft');
    }
  };

  // Create organization (save to DB)
  const handleCreate = async () => {
    // Only Organization Name is required — code is auto-generated, everything else optional
    if (!formData.establishmentName?.trim()) {
      alert('Organization Name is required');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use provided code or generate from name if not provided
      const code = formData.code || (formData.establishmentName
        ? formData.establishmentName.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10) + '-' + Date.now().toString().slice(-4)
        : 'EST-' + Date.now().toString().slice(-4));

      // Prepare data matching backend model
      const establishmentData = {
        name: formData.establishmentName,
        code: code,
        branch: formData.mainLocation || 'Main',
        address: formData.mainAddress || '',
        city: formData.mainLocation || '',
        state: '',
        zipCode: '',
        departments: JSON.stringify(departments),
        contractStartDate: formData.contractStartDate,
        contractValidTill: formData.contractValidTill,
        tenure: formData.tenure,
        // Tax/Compliance fields
        gstin: formData.gstin || null,
        pan: formData.pan || null,
        esicCode: formData.esicCode || null,
        epfCode: formData.epfCode || null,
        // Bank account fields
        bankName: formData.bankName || null,
        bankBranch: formData.bankBranch || null,
        bankAccountNumber: formData.bankAccountNumber || null,
        bankIfsc: formData.bankIfsc || null,
        // Extended fields
        mainLocation: formData.mainLocation,
        mainAddress: formData.mainAddress,
        mainTelephone: formData.mainTelephone,
        mainFax: formData.mainFax,
        mainEmail: formData.mainEmail,
        registeredLocation: formData.registeredLocation,
        registeredAddress: formData.registeredAddress,
        registeredTelephone: formData.registeredTelephone,
        registeredFax: formData.registeredFax,
        registeredEmail: formData.registeredEmail,
        employerName: formData.employerName,
        employerDesignation: formData.employerDesignation,
        employerResidentialAddress: formData.employerResidentialAddress,
        employerTelephone: formData.employerTelephone,
        employerMobile: formData.employerMobile,
        employerEmail: formData.employerEmail,
        managerName: formData.managerName,
        managerDesignation: formData.managerDesignation,
        managerResidentialAddress: formData.managerResidentialAddress,
        managerTelephone: formData.managerTelephone,
        managerMobile: formData.managerMobile,
        managerEmail: formData.managerEmail,
        authSignatoryName: formData.authSignatoryName,
        authSignatoryDesignation: formData.authSignatoryDesignation
      };

      // Call API
      const response = await establishmentAPI.create(establishmentData);
      const establishmentId = response.id;

      // Upload pending attachments if any
      if (pendingAttachments.length > 0) {
        setUploadMessage({ type: 'info', text: `Uploading ${pendingAttachments.length} file(s)...` });
        let uploadedCount = 0;
        let failedCount = 0;

        for (const attachment of pendingAttachments) {
          try {
            await establishmentAPI.uploadAttachment(attachment.file, establishmentId, attachment.category);
            uploadedCount++;
          } catch (error) {
            console.error(`Failed to upload ${attachment.file.name}:`, error);
            failedCount++;
          }
        }

        if (failedCount > 0) {
          setUploadMessage({ type: 'warning', text: `Uploaded ${uploadedCount}/${pendingAttachments.length} files. ${failedCount} failed.` });
        } else {
          setUploadMessage({ type: 'success', text: `All ${uploadedCount} file(s) uploaded successfully!` });
        }
      }

      // Clear draft from localStorage
      localStorage.removeItem(DRAFT_KEY);

      const message = pendingAttachments.length > 0
        ? `✓ Organization added successfully! ID: ${establishmentId}\n\n${pendingAttachments.length} file(s) uploaded.`
        : `✓ Organization added successfully! ID: ${establishmentId}\n\nYou can upload company logos, images, and documents by going to the "Update Organization" section.`;

      notifySavedAndReload(message);

      // Reset form
      setFormData({
        establishmentName: '',
        contractStartDate: getLocalToday(),
        contractValidTill: '',
        tenure: '',
        gstin: '',
        pan: '',
        esicCode: '',
        epfCode: '',
        bankName: '',
        bankBranch: '',
        bankAccountNumber: '',
        bankIfsc: '',
        mainLocation: '',
        mainAddress: '',
        mainTelephone: '',
        mainFax: '',
        mainEmail: '',
        registeredLocation: '',
        registeredAddress: '',
        registeredTelephone: '',
        registeredFax: '',
        registeredEmail: '',
        employerName: '',
        employerDesignation: '',
        employerResidentialAddress: '',
        employerTelephone: '',
        employerMobile: '',
        employerEmail: '',
        managerName: '',
        managerDesignation: '',
        managerResidentialAddress: '',
        managerTelephone: '',
        managerMobile: '',
        managerEmail: '',
        authSignatoryName: '',
        authSignatoryDesignation: 'Authorised Signatory',
        authSignatoryResidentialAddress: '',
        authSignatoryTelephone: '',
        authSignatoryMobile: '',
        authSignatoryEmail: ''
      });
      setDepartments([]);
      setNewDepartment('');
      setPendingAttachments([]);
      setSelectedCategory(null);
      setUploadMessage({ type: '', text: '' });
      setActiveTab('basic');
    } catch (error) {
      console.error('Error adding organization:', error);
      alert('❌ Error adding organization: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Attachment handling functions
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    for (const file of files) {
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setUploadMessage({ type: 'error', text: `${file.name} is too large. Maximum size is 10MB` });
        continue;
      }

      // Optional: Validate file type (currently allowing all file types)
      // Uncomment below to restrict file types
      // const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.pdf', '.doc', '.docx', '.txt', '.csv', '.xls', '.xlsx'];
      // const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      // if (!allowedExtensions.includes(fileExt)) {
      //   setUploadMessage({ type: 'error', text: `${file.name} has an invalid file type` });
      //   continue;
      // }

      // Add to pending attachments
      setPendingAttachments(prev => [...prev, { file, category: selectedCategory }]);
      const categoryText = selectedCategory ? ` as ${selectedCategory}` : ' as general';
      setUploadMessage({ type: 'success', text: `${file.name} added${categoryText}` });
    }

    // Reset file input and category
    e.target.value = '';
    setSelectedCategory(null);

    // Clear message after 3 seconds
    setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
  };

  const handleRemoveAttachment = (index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
    setUploadMessage({ type: 'success', text: 'File removed from upload queue' });
    setTimeout(() => setUploadMessage({ type: '', text: '' }), 3000);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return '🖼️';
    } else if (ext === 'pdf') {
      return '📄';
    } else if (['doc', 'docx'].includes(ext)) {
      return '📝';
    } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return '📊';
    } else if (ext === 'txt') {
      return '📋';
    }
    return '📎';
  };

  const handleNext = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
    }
  };

  const isLastTab = activeTab === 'management';

  return (
    <div className="new-employee-container">
      <h2 className="new-employee-header">Add New Organization</h2>

      <div className="new-employee-form">
        {/* Tabs */}
        <div className="new-employee-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`new-employee-tab ${activeTab === tab.id ? 'active' : ''}`}
              >
                <Icon size={16} />
                {tab.name}
                {activeTab === tab.id && (
                  <div className="new-employee-tab-indicator"></div>
                )}
              </button>
            );
          })}
        </div>

        <div className="new-employee-content">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Basic Information</h3>
              <p className="new-employee-section-description">Enter the organization's basic details.</p>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    Name of the Organization                  </label>
                  <input
                    type="text"
                    value={formData.establishmentName}
                    onChange={(e) => handleInputChange('establishmentName', e.target.value)}
                    className="new-employee-input"
                    placeholder="e.g., Nestien Shipment Management"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Organization Code will be auto-generated from the name
                  </p>
                </div>
              </div>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    Contract Start Date                  </label>
                  <DateInput
                    value={formData.contractStartDate}
                    onChange={(e) => handleInputChange('contractStartDate', e.target.value)}
                    className="new-employee-input"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Date when the contract begins
                  </p>
                </div>

                <div>
                  <label className="new-employee-field-label">
                    Contract Valid Till                  </label>
                  <DateInput
                    value={formData.contractValidTill}
                    onChange={(e) => handleInputChange('contractValidTill', e.target.value)}
                    className="new-employee-input"
                    min={formData.contractStartDate}
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Date when the contract expires
                  </p>
                </div>
              </div>

              {/* Auto-calculated Tenure */}
              {formData.tenure && (
                <div className="new-employee-field-group" style={{marginTop: '-0.5rem'}}>
                  <div style={{gridColumn: '1 / -1'}}>
                    <label className="new-employee-field-label">
                      Contract Tenure (Auto-calculated)
                    </label>
                    <div style={{
                      padding: '0.75rem 1rem',
                      background: '#f0fdf4',
                      border: '1.5px solid #10b981',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#059669'
                    }}>
                      {formData.tenure}
                    </div>
                  </div>
                </div>
              )}

              {/* Tax/Compliance Information */}
              <h3 className="new-employee-section-header" style={{marginTop: '2rem'}}>Tax & Compliance Information</h3>
              <p className="new-employee-section-description">Enter GSTIN, PAN, ESIC and EPF codes for invoicing and compliance.</p>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">
                    GSTIN (GST Identification Number)
                  </label>
                  <input
                    type="text"
                    value={formData.gstin}
                    onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                    className="new-employee-input"
                    placeholder="e.g., 29AACFE8938R1ZG"
                    maxLength={15}
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    15-character alphanumeric code
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">
                    PAN (Permanent Account Number)
                  </label>
                  <input
                    type="text"
                    value={formData.pan}
                    onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                    className="new-employee-input"
                    placeholder="e.g., AACFE8938R"
                    maxLength={10}
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    10-character alphanumeric code
                  </p>
                </div>
              </div>

              <div className="new-employee-field-group two-column">
                <div>
                  <label className="new-employee-field-label">
                    ESIC Code
                  </label>
                  <input
                    type="text"
                    value={formData.esicCode}
                    onChange={(e) => handleInputChange('esicCode', e.target.value)}
                    className="new-employee-input"
                    placeholder="e.g., 41000263690000999"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    ESIC organization code
                  </p>
                </div>
                <div>
                  <label className="new-employee-field-label">
                    EPF Code
                  </label>
                  <input
                    type="text"
                    value={formData.epfCode}
                    onChange={(e) => handleInputChange('epfCode', e.target.value)}
                    className="new-employee-input"
                    placeholder="e.g., VDBRD0065200000"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    EPF organization code
                  </p>
                </div>
              </div>

              {/* Bank Account Details Section */}
              <h3 className="new-employee-section-header" style={{ marginTop: '2rem' }}>Bank Account Details</h3>
              <p className="new-employee-section-description">Bank details for invoice payments.</p>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                    className="new-employee-input"
                    placeholder="e.g., State Bank of India"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">
                    Branch Name
                  </label>
                  <input
                    type="text"
                    value={formData.bankBranch}
                    onChange={(e) => handleInputChange('bankBranch', e.target.value)}
                    className="new-employee-input"
                    placeholder="e.g., Main Branch, Mumbai"
                  />
                </div>
              </div>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    Account Number
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccountNumber}
                    onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                    className="new-employee-input"
                    placeholder="e.g., 1234567890123456"
                  />
                </div>
                <div>
                  <label className="new-employee-field-label">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    value={formData.bankIfsc}
                    onChange={(e) => handleInputChange('bankIfsc', e.target.value.toUpperCase())}
                    className="new-employee-input"
                    placeholder="e.g., SBIN0001234"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    11-character IFSC code
                  </p>
                </div>
              </div>

              {/* Attachments Section */}
              <div className="new-employee-field-group" style={{ marginTop: '2rem' }}>
                <h3 className="new-employee-section-header">Attachments (Optional)</h3>
                <p className="new-employee-section-description">Upload company logos, signatures, and documents. Files will be uploaded after organization creation.</p>

                <div className="establishment-attachments-section" style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  border: '1px dashed var(--theme-primary)',
                  borderRadius: '0.5rem',
                  background: 'var(--theme-surface)'
                }}>
                  {/* Category Selection */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                      File Category
                    </label>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="fileCategoryAdd"
                          checked={selectedCategory === null}
                          onChange={() => setSelectedCategory(null)}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>General</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="fileCategoryAdd"
                          checked={selectedCategory === 'logo'}
                          onChange={() => setSelectedCategory('logo')}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>Logo</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="fileCategoryAdd"
                          checked={selectedCategory === 'signature'}
                          onChange={() => setSelectedCategory('signature')}
                          style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>Signature</span>
                      </label>
                    </div>
                  </div>

                  {/* Upload Button */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label
                      htmlFor="new-establishment-attachment-upload"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.625rem 1.25rem',
                        cursor: 'pointer',
                        background: 'var(--theme-surface, #ffffff)',
                        color: '#059669',
                        border: '2px solid #059669',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0fdf4';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--theme-surface, #ffffff)';
                      }}
                    >
                      <FileText size={16} />
                      Choose Files
                    </label>
                    <input
                      id="new-establishment-attachment-upload"
                      type="file"
                      multiple
                      accept="*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      All file types supported. Max 10MB per file.
                    </p>
                  </div>

                  {/* Upload Message */}
                  {uploadMessage.text && (
                    <div style={{
                      padding: '0.75rem',
                      borderRadius: '0.375rem',
                      marginBottom: '1rem',
                      background: uploadMessage.type === 'success' ? '#d1fae5' : uploadMessage.type === 'error' ? '#fee2e2' : '#dbeafe',
                      color: uploadMessage.type === 'success' ? '#065f46' : uploadMessage.type === 'error' ? '#991b1b' : '#1e40af',
                      border: `1px solid ${uploadMessage.type === 'success' ? '#10b981' : uploadMessage.type === 'error' ? '#ef4444' : '#3b82f6'}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem'
                    }}>
                      {uploadMessage.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                      {uploadMessage.text}
                    </div>
                  )}

                  {/* Pending Attachments List */}
                  {pendingAttachments.length > 0 ? (
                    <div>
                      <h4 style={{
                        color: '#059669',
                        fontWeight: '600',
                        marginBottom: '0.75rem',
                        fontSize: '0.875rem'
                      }}>
                        Files to Upload ({pendingAttachments.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {pendingAttachments.map((attachment, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              background: 'var(--theme-surface, #ffffff)',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.375rem'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              flex: 1
                            }}>
                              <span style={{ fontSize: '1.5rem' }}>
                                {getFileIcon(attachment.file.name)}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{
                                    color: '#374151',
                                    fontWeight: '500',
                                    fontSize: '0.875rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {attachment.file.name}
                                  </span>
                                  <span style={{
                                    padding: '0.125rem 0.5rem',
                                    background: !attachment.category ? '#f8f9fa' : attachment.category === 'logo' ? '#dbeafe' : '#fce7f3',
                                    color: !attachment.category ? '#6b7280' : attachment.category === 'logo' ? '#1e40af' : '#9f1239',
                                    borderRadius: '0.25rem',
                                    fontSize: '0.625rem',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {attachment.category || 'general'}
                                  </span>
                                </div>
                                <p style={{
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  marginTop: '0.125rem'
                                }}>
                                  {formatFileSize(attachment.file.size)}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveAttachment(index)}
                              style={{
                                color: '#ef4444',
                                cursor: 'pointer',
                                background: 'none',
                                border: 'none',
                                padding: '0.375rem',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'color 0.2s'
                              }}
                              title="Remove file"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '0.875rem'
                    }}>
                      No files selected yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Departments Tab */}
          {activeTab === 'departments' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Departments</h3>
              <p className="new-employee-section-description">Add one or more departments for this organization.</p>

              <div className="new-employee-field-group">
                <div>
                  <label className="new-employee-field-label">
                    <Briefcase size={16} style={{ marginRight: '6px' }} />
                    Add Department                  </label>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    <input
                      type="text"
                      value={newDepartment}
                      onChange={(e) => setNewDepartment(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="new-employee-input"
                      placeholder="e.g., Sales, HR, Operations"
                      style={{ flex: 1 }}
                    />
                    <button
                      onClick={handleAddDepartment}
                      className="new-employee-btn-next"
                      style={{
                        minWidth: 'auto',
                        padding: '0.625rem 1.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      type="button"
                    >
                      <Plus size={16} />
                      Add
                    </button>
                  </div>
                </div>

                {/* Department List */}
                {departments.length > 0 && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <h4 style={{
                      color: '#059669',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      fontSize: '0.875rem'
                    }}>
                      Departments ({departments.length})
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '0.75rem'
                    }}>
                      {departments.map((dept, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.625rem 1rem',
                            background: '#f0fdf4',
                            border: '1.5px solid #10b981',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: '#059669'
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Briefcase size={14} />
                            {dept}
                          </span>
                          <button
                            onClick={() => handleRemoveDepartment(dept)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#dc2626',
                              transition: 'color 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.color = '#991b1b'}
                            onMouseOut={(e) => e.currentTarget.style.color = '#dc2626'}
                            type="button"
                            title="Remove department"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {departments.length === 0 && (
                  <div style={{
                    marginTop: '1.5rem',
                    padding: '2rem',
                    background: 'var(--theme-surface)',
                    border: '2px dashed #d1d5db',
                    borderRadius: '0.5rem',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                  }}>
                    <Briefcase size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.5 }} />
                    <p>No departments added yet. Add departments using the input above.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Addresses Tab */}
          {activeTab === 'addresses' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Postal Addresses</h3>
              <p className="new-employee-section-description">Enter main organization and registered office addresses.</p>

              {/* Main Organization Address */}
              <div className="new-employee-field-group">
                <h4 style={{ color: '#059669', fontWeight: '600', marginBottom: '1rem' }}>a) Main Organization</h4>
                
                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">
                      Location/City                    </label>
                    <input
                      type="text"
                      value={formData.mainLocation}
                      onChange={(e) => handleInputChange('mainLocation', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., Bangalore"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">Telephone</label>
                    <input
                      type="tel"
                      value={formData.mainTelephone}
                      onChange={(e) => handleInputChange('mainTelephone', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., 22 24148999"
                    />
                  </div>
                </div>

                <div>
                  <label className="new-employee-field-label">
                    Full Address                  </label>
                  <textarea
                    value={formData.mainAddress}
                    onChange={(e) => handleInputChange('mainAddress', e.target.value)}
                    className="new-employee-input"
                    rows="4"
                    placeholder="Site: Company Name Pvt. Ltd&#10;Block details, Tech Park&#10;Street, Area, City - PIN"
                  />
                </div>

                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">Fax</label>
                    <input
                      type="tel"
                      value={formData.mainFax}
                      onChange={(e) => handleInputChange('mainFax', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., 22 65118999"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">
                      E-mail                    </label>
                    <input
                      type="email"
                      value={formData.mainEmail}
                      onChange={(e) => handleInputChange('mainEmail', e.target.value)}
                      className="new-employee-input"
                      placeholder="info@company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Registered/Head Office Address */}
              <div className="new-employee-field-group">
                <h4 style={{ color: '#059669', fontWeight: '600', marginBottom: '1rem' }}>b) Registered / Head Office</h4>
                
                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">
                      Location/City                    </label>
                    <input
                      type="text"
                      value={formData.registeredLocation}
                      onChange={(e) => handleInputChange('registeredLocation', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., Mumbai"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">Telephone</label>
                    <input
                      type="tel"
                      value={formData.registeredTelephone}
                      onChange={(e) => handleInputChange('registeredTelephone', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., 22 24148999"
                    />
                  </div>
                </div>

                <div>
                  <label className="new-employee-field-label">
                    Full Address                  </label>
                  <textarea
                    value={formData.registeredAddress}
                    onChange={(e) => handleInputChange('registeredAddress', e.target.value)}
                    className="new-employee-input"
                    rows="4"
                    placeholder="Building details, Complex Name&#10;Area, Near Landmark&#10;Off: Road Name, Location&#10;City, State - PIN"
                  />
                </div>

                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">Fax</label>
                    <input
                      type="tel"
                      value={formData.registeredFax}
                      onChange={(e) => handleInputChange('registeredFax', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., 22 65118999"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">
                      E-mail                    </label>
                    <input
                      type="email"
                      value={formData.registeredEmail}
                      onChange={(e) => handleInputChange('registeredEmail', e.target.value)}
                      className="new-employee-input"
                      placeholder="info@company.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Employer Details Tab */}
          {activeTab === 'employer' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Employer/Responsible Person</h3>
              <p className="new-employee-section-description">Details of person responsible for conduct and control of the business.</p>

              <div className="new-employee-field-group">
                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">
                      Name                    </label>
                    <input
                      type="text"
                      value={formData.employerName}
                      onChange={(e) => handleInputChange('employerName', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., Suresh Kumar Raju"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">
                      Designation                    </label>
                    <input
                      type="text"
                      value={formData.employerDesignation}
                      onChange={(e) => handleInputChange('employerDesignation', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., Proprietor"
                    />
                  </div>
                </div>

                <div>
                  <label className="new-employee-field-label">
                    Residential Address                  </label>
                  <textarea
                    value={formData.employerResidentialAddress}
                    onChange={(e) => handleInputChange('employerResidentialAddress', e.target.value)}
                    className="new-employee-input"
                    rows="4"
                    placeholder="Flat No., Wing, Building Name&#10;Area, Near Landmark&#10;City, State - PIN"
                  />
                </div>

                <div className="new-employee-field-group three-column">
                  <div>
                    <label className="new-employee-field-label">Telephone</label>
                    <input
                      type="tel"
                      value={formData.employerTelephone}
                      onChange={(e) => handleInputChange('employerTelephone', e.target.value)}
                      className="new-employee-input"
                      placeholder="(O) 22 24148999"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">Mobile</label>
                    <input
                      type="tel"
                      value={formData.employerMobile}
                      onChange={(e) => handleInputChange('employerMobile', e.target.value)}
                      className="new-employee-input"
                      placeholder="9819731611"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">
                      E-mail                    </label>
                    <input
                      type="email"
                      value={formData.employerEmail}
                      onChange={(e) => handleInputChange('employerEmail', e.target.value)}
                      className="new-employee-input"
                      placeholder="info@company.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Management Tab */}
          {activeTab === 'management' && (
            <div className="animate-fadeIn">
              <h3 className="new-employee-section-header">Management Details</h3>
              <p className="new-employee-section-description">Manager and authorised signatory information.</p>

              {/* Manager Details */}
              <div className="new-employee-field-group">
                <h4 style={{ color: '#059669', fontWeight: '600', marginBottom: '1rem' }}>Manager</h4>
                
                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">
                      Name                    </label>
                    <input
                      type="text"
                      value={formData.managerName}
                      onChange={(e) => handleInputChange('managerName', e.target.value)}
                      className="new-employee-input"
                      placeholder="e.g., DASS DURAIRAJ"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">
                      Designation                    </label>
                    <input
                      type="text"
                      value={formData.managerDesignation}
                      onChange={(e) => handleInputChange('managerDesignation', e.target.value)}
                      className="new-employee-input"
                      placeholder="Manager"
                    />
                  </div>
                </div>

                <div>
                  <label className="new-employee-field-label">
                    Residential Address                  </label>
                  <textarea
                    value={formData.managerResidentialAddress}
                    onChange={(e) => handleInputChange('managerResidentialAddress', e.target.value)}
                    className="new-employee-input"
                    rows="3"
                    placeholder="Street address, Area&#10;City - PIN"
                  />
                </div>

                <div className="new-employee-field-group three-column">
                  <div>
                    <label className="new-employee-field-label">Telephone</label>
                    <input
                      type="tel"
                      value={formData.managerTelephone}
                      onChange={(e) => handleInputChange('managerTelephone', e.target.value)}
                      className="new-employee-input"
                      placeholder="(O)"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">Mobile</label>
                    <input
                      type="tel"
                      value={formData.managerMobile}
                      onChange={(e) => handleInputChange('managerMobile', e.target.value)}
                      className="new-employee-input"
                      placeholder="9886542008"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">
                      E-mail                    </label>
                    <input
                      type="email"
                      value={formData.managerEmail}
                      onChange={(e) => handleInputChange('managerEmail', e.target.value)}
                      className="new-employee-input"
                      placeholder="manager@company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Authorised Signatory (if different from Manager) */}
              <div className="new-employee-field-group">
                <h4 style={{ color: '#059669', fontWeight: '600', marginBottom: '1rem' }}>Authorised Signatory (if different)</h4>
                
                <div className="new-employee-field-group two-column">
                  <div>
                    <label className="new-employee-field-label">Name</label>
                    <input
                      type="text"
                      value={formData.authSignatoryName}
                      onChange={(e) => handleInputChange('authSignatoryName', e.target.value)}
                      className="new-employee-input"
                      placeholder="Leave blank if same as Manager"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">Designation</label>
                    <input
                      type="text"
                      value={formData.authSignatoryDesignation}
                      onChange={(e) => handleInputChange('authSignatoryDesignation', e.target.value)}
                      className="new-employee-input"
                      placeholder="Authorised Signatory"
                    />
                  </div>
                </div>

                <div>
                  <label className="new-employee-field-label">Residential Address</label>
                  <textarea
                    value={formData.authSignatoryResidentialAddress}
                    onChange={(e) => handleInputChange('authSignatoryResidentialAddress', e.target.value)}
                    className="new-employee-input"
                    rows="3"
                    placeholder="Leave blank if same as Manager"
                  />
                </div>

                <div className="new-employee-field-group three-column">
                  <div>
                    <label className="new-employee-field-label">Telephone</label>
                    <input
                      type="tel"
                      value={formData.authSignatoryTelephone}
                      onChange={(e) => handleInputChange('authSignatoryTelephone', e.target.value)}
                      className="new-employee-input"
                      placeholder="(O)"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">Mobile</label>
                    <input
                      type="tel"
                      value={formData.authSignatoryMobile}
                      onChange={(e) => handleInputChange('authSignatoryMobile', e.target.value)}
                      className="new-employee-input"
                      placeholder="Mobile number"
                    />
                  </div>
                  <div>
                    <label className="new-employee-field-label">E-mail</label>
                    <input
                      type="email"
                      value={formData.authSignatoryEmail}
                      onChange={(e) => handleInputChange('authSignatoryEmail', e.target.value)}
                      className="new-employee-input"
                      placeholder="signatory@company.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="new-employee-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem', gap: '1rem' }}>
            <button
              onClick={() => {
                const currentIndex = tabs.findIndex(t => t.id === activeTab);
                if (currentIndex > 0) {
                  setActiveTab(tabs[currentIndex - 1].id);
                }
              }}
              disabled={activeTab === 'basic'}
              className="new-employee-btn-previous"
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'basic' ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
                color: activeTab === 'basic' ? '#9ca3af' : '#374151',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: activeTab === 'basic' ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Previous
            </button>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {/* Save Draft Button - Always visible */}
              <button
                onClick={handleSubmit}
                className="new-employee-btn-previous"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: 'var(--theme-surface, #ffffff)',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                <Save size={16} />
                Save Draft
              </button>

              {/* Create Button - always visible; only Name is required */}
              <button
                onClick={handleCreate}
                disabled={isSubmitting}
                className="new-employee-btn-create"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  background: '#10b981',
                  color: 'var(--theme-surface, #ffffff)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                {isSubmitting ? (
                  <>
                    <div className="new-employee-spinner"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Create Organization
                  </>
                )}
              </button>

              {/* Next Button - Only if not last tab */}
              {!isLastTab && (
                <button
                  onClick={handleNext}
                  className="new-employee-btn-next"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: '#3b82f6',
                    color: 'var(--theme-surface, #ffffff)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEstablishment;
